import React, { useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { appConfig } from '../apiConfig'
import {
  Printer,
  User,
  CreditCard,
  Package,
  MapPin
} from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageProvider";

export default function LivreurRapport({ invoice }) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const factureRef = useRef();

  const formatDateTime = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "N/A";

  const formatStatus = (status) => {
    const statusMap = {
      paid: t('cors_livreur_report.paid'),
      pending_payment: t('cors_livreur_report.pending_payment'),
      cancelled: t('cors_livreur_report.cancelled'),
    };
    return (
      statusMap[status] ||
      (status ? status.charAt(0).toUpperCase() + status.slice(1) : t('cors_livreur_report.unknown'))
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_payment: "bg-yellow-100 text-yellow-700 border-yellow-200",
      paid: "bg-green-600 text-white border-green-200",
      cancelled: "bg-red-100 text-red-700 border-red-200",
    };
    const classes =
      statusConfig[status?.toLowerCase()] || "bg-gray-50 text-gray-700";

    return (
      <span
        className={`inline-flex items-center px-4 pb-0.5 pt-[3px] rounded-full text-[11px] font-medium border ${classes}`}
      >
        {formatStatus(status)}
      </span>
    );
  };

  const handlePrint = useReactToPrint({
    contentRef: factureRef,
    documentTitle: `Facture_Livreur_${invoice?.invoice_number || "N/A"}`,
    pageStyle: `
      @media print {
        .page { margin: 10mm; }
        body { -webkit-print-color-adjust: exact; }
        tr { page-break-inside: avoid; break-inside: avoid; }
        .print-break { page-break-after: always; }
      }
    `,
    onPrintError: () => toast.error(t('cors_livreur_report.print_error')),
  });

  const handlePrintClick = () => {
    if (!shouldRender) {
      setShouldRender(true);
      setTimeout(() => {
        handlePrint();
      }, 2000);
    } else if (invoice) {
      handlePrint();
    }
  };

  return (
    <div className="">
      <button
        onClick={handlePrintClick}
        disabled={loading}
        className="p-2 border-2 cursor-pointer border-gray-200 hover:border-[var(--color-primary-500)] text-gray-600 hover:text-[var(--color-primary-500)] rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        title={t('cors_livreur_report.print_invoice')}
      >
        <Printer className="w-3.5 h-3.5" />
      </button>

      {shouldRender && (
        <div className="hidden">
          <div
            ref={factureRef}
            className="relative bg-white text-black mx-auto "
            style={{
              width: "175mm",
              minHeight: "1040px",
              padding: "10mm 5mm",
              fontFamily: "Arial, sans-serif",
              fontSize: "12px",
            }}
          >
            {loading ? (
              <div className="flex justify-center items-center h-full">
                <div>{t('cors_livreur_report.loading_invoice')}</div>
              </div>
            ) : invoice ? (
              <>
                {/* HEADER */}
                <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-4">
                  <div>
                    <img src={`/images/${appConfig.slug}-logo.png`} width={150} alt="Logo" />
                  </div>
                  <div className="text-left">
                    <h1 className="text-xl font-bold">{t('cors_livreur_report.invoice_title')}</h1>
                    <p>N°: {invoice.invoice_number}</p>
                    <p>{t('cors_livreur_report.emitted_on')} {formatDateTime(invoice.created_at)}</p>
                    <div className="mt-2">{getStatusBadge(invoice.status)}</div>
                  </div>
                </div>

                {/* Livreur & Bank Info */}
                <div className="mb-8">
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="border border-gray-200 rounded-lg text-left p-4 bg-white">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="w-4 h-4 text-teal-600" />
                        <h3 className="text-sm font-bold">{t('cors_livreur_report.delivery_info')}</h3>
                      </div>
                      <p className="text-xs font-semibold">{t('cors_livreur_report.name')} {invoice.livreur?.name}</p>
                      <p className="text-xs">{t('cors_livreur_report.phone')} {invoice.livreur?.phone}</p>
                      <p className="text-xs">{t('cors_livreur_report.delivery_rate')} {parseFloat(invoice.livreur?.prix_delivery || 0).toFixed(2)} MAD</p>
                    </div>
                    <div className="border border-gray-200 rounded-lg text-left p-4 bg-white">
                      <div className="flex items-center gap-2 mb-2">
                        <CreditCard className="w-4 h-4 text-emerald-600" />
                        <h3 className="text-sm font-bold">{t('cors_livreur_report.payment')}</h3>
                      </div>
                      <p className="text-xs">RIB: {invoice.livreur?.rib || "N/A"}</p>
                      {invoice.payment_date && <p className="text-xs">{t('cors_livreur_report.paid_on')} {formatDateTime(invoice.payment_date)}</p>}
                      {invoice.payment_method && <p className="text-xs">{t('cors_livreur_report.method')} {invoice.payment_method}</p>}
                    </div>
                  </div>

                  <h3 className="text-sm font-bold mb-2 flex items-center gap-1">
                    <Package className="w-4 h-4 text-purple-600" /> {t('cors_livreur_report.delivered_orders')}
                  </h3>
                  <table className="w-full border-collapse mb-6">
                    <thead>
                      <tr className="bg-gray-50">
                        <th style={thStyle}>{t('cors_livreur_report.code')}</th>
                        <th style={thStyle}>{t('cors_livreur_report.client')}</th>
                        <th style={thStyle}>{t('cors_livreur_report.city')}</th>
                        <th style={thStyle}>{t('cors_livreur_report.weight_vol')}</th>
                        <th style={thStyle}>{t('cors_livreur_report.delivery_rate')}</th>
                        <th style={thStyle}>{t('cors_livreur_report.order_total')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.livreur_invoice_orders?.map((lio, index) => (
                        <tr key={lio.id}>
                          <td style={tdStyle} className="text-center">
                            <div className="font-mono text-xs bg-purple-100 px-2 py-1 rounded text-purple-700">
                              #{lio.order?.code}
                            </div>
                          </td>
                          <td style={tdStyle} className="text-center">{lio.order?.nom}</td>
                          <td style={tdStyle} className="text-center">{lio.order?.ville}</td>
                          <td style={tdStyle} className="text-center">-</td>
                          <td style={tdStyle} className="text-center">{parseFloat(lio.order?.delivery_price || 0).toFixed(2)} MAD</td>
                          <td style={tdStyle} className="text-center">{parseFloat(lio.order?.total || 0).toFixed(2)} MAD</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Totals */}
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 flex justify-between items-center">
                    <div>
                    <div className="text-sm text-gray-700">{t('cors_livreur_report.total_to_pay')}</div>
                    <div className="text-xs text-gray-500">{t('cors_livreur_report.orders_delivered_count', { count: invoice.livreur_invoice_orders?.length || 0 })}</div>
                    </div>
                    <div className="text-2xl font-bold text-teal-700">{Number.parseFloat(invoice.total || 0).toFixed(2)} MAD</div>
                  </div>
                </div>

                {/* Footer */}
                <div className="absolute bottom-0 text-center text-[10px] text-gray-500 border-t border-gray-200 pt-4 mt-4 w-full">
                  {t('cors_livreur_report.footer_thanks')}<br />
                  Adresse: N°53, Césarée, Mazalia, Centre commercial -
                  Derb Omar, Rue Mohammed VI, Casablanca
                  Téléphone: 0708787525 -
                  Email: nextlead1397@gmail.com -
                  Website: www.nextlead.ma<br />
                </div>
              </>
            ) : (
              <div className="flex justify-center items-center h-full">
                <div>{t('cors_livreur_report.no_invoice')}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  border: "1px solid #ddd",
  padding: "6px 2px",
  textAlign: "center",
  fontSize: "11px",
};
const tdStyle = {
  border: "1px solid #ddd",
  padding: "4px 2px",
  fontSize: "11px",
};
